import { S1Endpoint, S1InstallerType } from 'common/graphqlTypes';
import { formatTimestamp, getRelativeFromNow } from 'common/util/time';
import { formatBytes } from 'components/billingAndUsage/utils';

export interface EndpointGeneralFieldConfiguration {
  // default value such as 'N/A'
  defaultValue: string;
  // this method decides whether to show the field or not
  isVisible: (data: S1Endpoint | undefined) => boolean;
  key: string;
  // the field title
  title: string;
  // this method gets the endpoint data and generates the field JSX
  value: (data: S1Endpoint | undefined) => JSX.Element | null;
}

const DEFAULT_FIELD_CONFIGURATION = {
  defaultValue: 'N/A',
  isVisible: () => true,
};

const INSTALLER_TYPES_TITLES: ReadonlyArray<{ title: String; name: S1InstallerType }> = [
  { title: 'N/A', name: S1InstallerType.unknown },
  { title: 'BSX', name: S1InstallerType.bsx },
  { title: 'DEB', name: S1InstallerType.deb },
  { title: 'EXE', name: S1InstallerType.exe },
  { title: 'MSI', name: S1InstallerType.msi },
  { title: 'PKG', name: S1InstallerType.pkg },
  { title: 'RPM', name: S1InstallerType.rpm },
  { title: 'TAR', name: S1InstallerType.tar },
  { title: 'ZIP', name: S1InstallerType.zip },
  { title: 'IMG', name: S1InstallerType.img },
];

export function createEndpointGeneralFieldsConfiguration(timezone: string | null): EndpointGeneralFieldConfiguration[] {
  return [
    {
      ...DEFAULT_FIELD_CONFIGURATION,
      key: 'lastActiveDate',
      title: 'Last active',
      value: (data: S1Endpoint | undefined) => {
        if (data?.isActive) {
          return <>Last 4 minutes</>;
        }
        if (data?.lastActiveDate) {
          const lastActiveDate = new Date(data.lastActiveDate).getTime();
          return (
            <>
              {formatTimestamp(lastActiveDate, null)} ({getRelativeFromNow(lastActiveDate)})
            </>
          );
        }
        return null;
      },
    },
    {
      ...DEFAULT_FIELD_CONFIGURATION,
      key: 'infected',
      title: 'Health status',
      value: (data: S1Endpoint | undefined) => <>{data?.infected ? 'Infected' : 'Healthy'}</>,
    },
    {
      ...DEFAULT_FIELD_CONFIGURATION,
      key: 'lastLoggedInUserName',
      title: 'Last logged in',
      value: (data: S1Endpoint | undefined) => (data?.lastLoggedInUserName ? <>{data.lastLoggedInUserName}</> : null),
    },
    {
      ...DEFAULT_FIELD_CONFIGURATION,
      key: 'agentVersion',
      title: 'Agent version',
      value: (data: S1Endpoint | undefined) =>
        data?.agentVersion ? (
          <>
            {data.agentVersion} {data?.isUpToDate && <span className="online">Updated</span>}
          </>
        ) : null,
    },
    {
      ...DEFAULT_FIELD_CONFIGURATION,
      key: 'scanStartedAt',
      title: 'Full Disk Scan',
      value: (data: S1Endpoint | undefined) => {
        switch (data?.scanStatus) {
          case 'started':
            return data?.scanStartedAt ? (
              <>In Progress ${formatTimestamp(new Date(data.scanStartedAt).getTime(), timezone)}</>
            ) : null;
          case 'aborted':
            return data?.scanAbortedAt ? (
              <>Aborted ${formatTimestamp(new Date(data.scanAbortedAt).getTime(), timezone)}</>
            ) : null;
          case 'finished':
            return data?.scanFinishedAt ? (
              <>Completed ${formatTimestamp(new Date(data.scanFinishedAt).getTime(), timezone)}</>
            ) : null;
          default:
            return null;
        }
      },
    },
    {
      ...DEFAULT_FIELD_CONFIGURATION,
      key: 'totalMemory',
      title: 'Memory',
      value: (data: S1Endpoint | undefined) =>
        data?.totalMemory ? <>{formatBytes(data.totalMemory * 1024 * 1024, 2)}</> : null,
    },
    {
      ...DEFAULT_FIELD_CONFIGURATION,
      key: 'cpuId',
      title: 'CPU',
      value: (data: S1Endpoint | undefined) =>
        data?.cpuCount && data?.cpuId ? (
          <>
            {data.cpuCount} X {data.cpuId}
          </>
        ) : null,
    },
    {
      ...DEFAULT_FIELD_CONFIGURATION,
      key: 'coreCount',
      title: 'Core count',
      value: (data: S1Endpoint | undefined) => (data?.coreCount ? <>{data.coreCount}</> : null),
    },
    {
      ...DEFAULT_FIELD_CONFIGURATION,
      key: 'externalId',
      title: 'Customer identifier',
      value: (data: S1Endpoint | undefined) => (data?.externalId ? <>{data.externalId}</> : null),
    },
    {
      ...DEFAULT_FIELD_CONFIGURATION,
      key: 'rangerVersion',
      title: 'Ranger Version',
      value: (data: S1Endpoint | undefined) => (data?.rangerVersion ? <>{data.rangerVersion}</> : null),
      isVisible: (data: S1Endpoint | undefined) => data?.machineType !== 'storage',
    },
    {
      ...DEFAULT_FIELD_CONFIGURATION,
      key: 'installerType',
      title: 'Installer Type',
      value: (data: S1Endpoint | undefined) => {
        const installerType = INSTALLER_TYPES_TITLES.find(
          (installerType) => installerType.name === data?.installerType,
        )?.title;
        return installerType ? <>{installerType}</> : null;
      },
    },
    {
      ...DEFAULT_FIELD_CONFIGURATION,
      key: 'firewallEnabled',
      title: 'Firewall status',
      value: (data: S1Endpoint | undefined) =>
        !data || data.machineType === 'storage' ? null : (
          <span className={data?.firewallEnabled ? 'online' : 'offline'}>
            {data?.firewallEnabled ? 'Enabled' : 'Disabled'}
          </span>
        ),
    },
    {
      ...DEFAULT_FIELD_CONFIGURATION,
      key: 'encryptedApplications',
      title: 'Disk encryption',
      value: (data: S1Endpoint | undefined) =>
        data && data.osType !== 'linux' && data.machineType !== 'storage' ? (
          <>{data?.encryptedApplications ? 'On' : 'Off'}</>
        ) : null,
    },
    {
      ...DEFAULT_FIELD_CONFIGURATION,
      key: 'uuid',
      title: 'UUID',
      value: (data: S1Endpoint | undefined) => (data?.uuid ? <>{data.uuid}</> : null),
    },
    {
      ...DEFAULT_FIELD_CONFIGURATION,
      key: 'isActive',
      title: 'Console connectivity',
      value: (data: S1Endpoint | undefined) =>
        data ? (
          <span className={data.isActive ? 'online' : 'offline'}>{data.isActive ? 'Online' : 'Offline'}</span>
        ) : null,
    },
    {
      ...DEFAULT_FIELD_CONFIGURATION,
      key: 'networkStatus',
      title: 'Network status',
      value: (data: S1Endpoint | undefined) => {
        const isConnected = data?.networkStatus !== 'connected';
        return data ? (
          <span className={isConnected ? 'offline' : 'online'}>{isConnected ? 'Connected' : 'Disconnected'}</span>
        ) : null;
      },
    },
    {
      ...DEFAULT_FIELD_CONFIGURATION,
      key: 'configurableNetworkQuarantine',
      title: 'Configurable Network Quarantine',
      value: (data: S1Endpoint | undefined) =>
        data ? (
          <span className={data.networkQuarantineEnabled ? 'online' : 'offline'}>
            {data.machineType === 'storage' ? 'N/A' : data.networkQuarantineEnabled ? 'Enabled' : 'Disabled'}
          </span>
        ) : null,
    },
    {
      ...DEFAULT_FIELD_CONFIGURATION,
      key: 'domain',
      title: 'Domain',
      value: (data: S1Endpoint | undefined) => (data?.domain ? <>{data.domain}</> : null),
    },
    {
      ...DEFAULT_FIELD_CONFIGURATION,
      key: 'registeredAt',
      title: 'Subscribed on',
      value: (data: S1Endpoint | undefined) => {
        if (!data?.registeredAt) {
          return null;
        }
        const registeredAt = new Date(data.registeredAt).getTime();
        return <>{formatTimestamp(registeredAt, timezone)}</>;
      },
    },
    {
      ...DEFAULT_FIELD_CONFIGURATION,
      key: 'osStartTime',
      title: 'Last Reboot',
      value: (data: S1Endpoint | undefined) => {
        if (!data?.osStartTime) {
          return null;
        }
        const osStartTime = new Date(data.osStartTime).getTime();
        return <>{formatTimestamp(osStartTime, timezone)}</>;
      },
    },
    {
      ...DEFAULT_FIELD_CONFIGURATION,
      key: 'externalIp',
      title: 'Console visible IP',
      value: (data: S1Endpoint | undefined) => (data?.externalIp ? <>{data.externalIp}</> : null),
    },
    {
      ...DEFAULT_FIELD_CONFIGURATION,
      key: 'networkInterfaces',
      title: 'IP Address',
      value: (data: S1Endpoint | undefined) => {
        if (!data?.networkInterfaces) {
          return null;
        }
        const networkIps = data?.networkInterfaces
          ?.filter((networkInterface) => networkInterface?.inet?.length > 0)
          .map((networkInterface) => networkInterface.inet[0]);
        return networkIps?.length > 0 ? <>{networkIps?.join(', ')}</> : null;
      },
    },
    {
      ...DEFAULT_FIELD_CONFIGURATION,
      key: 'locationType',
      title: 'Locations',
      value: (data: S1Endpoint | undefined) => {
        if (data && data.machineType !== 'storage') {
          if (data.locationType === 'specific') {
            const specificLocation =
              data.locations.length === 1 ? data.locations[0].name : `${data.locations.length} locations`;
            return <>{specificLocation}</>;
          } else {
            return data.locationType ? <>{data.locationType}</> : null;
          }
        }
        return null;
      },
    },
    {
      ...DEFAULT_FIELD_CONFIGURATION,
      key: 'serialNumber',
      title: 'Serial Number',
      value: (data: S1Endpoint | undefined) => (data?.serialNumber ? <>{data.serialNumber}</> : null),
    },
  ];
}
